import React, { useId, useMemo } from "react";
import { Box, CircularProgress, Collapse, Divider, IconButton, Tooltip, Typography, alpha } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { REVIEW_STATUSES } from "../AuditHub/constants";
import { formatDateTime } from "../AuditHub/utils";
import { AH_ACCENT, AH_BORDER, AH_MUTED, AH_TEXT, AH_TRACK, AH_WHITE, MetaSeparator, cardShellSx } from "../AuditHub/theme";
import {
  CardHeader,
  CardShell,
  FAILED_COLOR,
  FAILED_TEXT_COLOR,
  PASSED_COLOR,
  PASSED_TEXT_COLOR,
  Tile,
  formatCount,
  labelSx,
} from "./AuditCardParts";
import { complianceScoreParts, readComponents } from "./auditMetrics";

const PENDING_SCAN = "Pending automated scan completion";
const accountGridSx = {
  display: "grid",
  gap: 1,
  alignItems: "stretch",
  gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(200px, 0.85fr) minmax(0, 2fr)" },
};

const sideStackSx = {
  display: "flex",
  flexDirection: "column",
  gap: 1,
  minWidth: 0,
  height: { md: "100%" },
};

const compactTileSx = { px: 1.25, py: 0.75, gap: 0.5 };

const Quiet = ({ children }) => (
  <Typography sx={{ fontSize: 13, color: AH_MUTED, overflowWrap: "anywhere" }}>{children}</Typography>
);

const metricRowSx = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  columnGap: 1,
  alignItems: "baseline",
};

const metricHeadSx = {
  fontSize: 13,
  fontWeight: 600,
  textAlign: "center",
  lineHeight: 1.3,
};

const metricValueSx = {
  fontSize: 16,
  fontWeight: 700,
  color: AH_TEXT,
  textAlign: "center",
  lineHeight: 1.2,
  fontVariantNumeric: "tabular-nums",
};

const isStatusMap = (value) =>
  Boolean(value) &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  REVIEW_STATUSES.some((status) => Object.prototype.hasOwnProperty.call(value, status.value));

/** Review-status bars are this account's counts. The assessment map covers every account. */
const statusCountsForAccount = (audit, accountNumber) => {
  if (!accountNumber) return null;
  const accounts = Array.isArray(audit?.accounts) ? audit.accounts : [];
  const account = accounts.find((item) => String(item?.accountNumber) === String(accountNumber));
  return isStatusMap(account?.statusCounts) ? account.statusCounts : null;
};

/** Every review status on the map, including zeros. */
const reviewRows = (statusCounts) => {
  if (!statusCounts) return [];
  return REVIEW_STATUSES.map((status) => {
    const count = Number(statusCounts[status.value]);
    return { ...status, count: Number.isFinite(count) && count > 0 ? count : 0 };
  });
};

const dash = "—";

/** The ring is passed out of total. Gray is the rest of the total; N/A is not on it. */
const ScoreRing = ({ score, total, passed }) => {
  const shown = Number.isFinite(score) ? `${score.toFixed(2)}%` : dash;
  const value = Number.isFinite(score) ? Math.min(100, Math.max(0, score)) : 0;
  return (
    <Box
      role="img"
      aria-label={Number.isFinite(score) ? `Compliance score ${shown}, ${passed} passed of ${total}` : "Compliance score unavailable"}
      sx={{ position: "relative", width: "100%", aspectRatio: "1", containerType: "inline-size" }}
    >
      <CircularProgress variant="determinate" value={100} size="100%" thickness={4} sx={{ color: AH_TRACK, position: "absolute", left: 0, top: 0 }} />
      <CircularProgress
        variant="determinate"
        value={value}
        size="100%"
        thickness={4}
        sx={{ color: PASSED_TEXT_COLOR, position: "absolute", left: 0, top: 0 }}
      />
      <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography sx={{ fontSize: "clamp(18px, 16cqw, 24px)", fontWeight: 700, color: AH_TEXT, lineHeight: 1, textAlign: "center" }}>
          {shown}
        </Typography>
      </Box>
    </Box>
  );
};

const UNDECIDED_HINT =
  "This count represents manual controls with a review status of Not Started, Pending Artifacts, Ready for Review, Under Review, or Needs More Info.";

const scoreFacts = (total, passed, failed, undecided) => [
  { label: "Total", value: total, color: AH_MUTED },
  { label: "Passed", value: passed, color: PASSED_TEXT_COLOR },
  { label: "Failed", value: failed, color: FAILED_TEXT_COLOR },
  { label: "Undecided", value: undecided, color: AH_MUTED, hint: UNDECIDED_HINT },
];

export const ComplianceScoreCard = ({ total = 0, passed = 0, failed = 0, undecided = 0, known, unavailable, pendingScan, fill = false }) => {
  const score = total > 0 ? (passed / total) * 100 : null;
  return (
    <Tile
      label="Compliance Score"
      align="start"
      sx={{ ...compactTileSx, ...(fill ? { height: "100%", alignSelf: "stretch", minHeight: 0 } : { alignSelf: "start", height: "auto" }) }}
    >
      {!known ? (
        <Quiet>{unavailable || dash}</Quiet>
      ) : (
        <Box sx={{ flex: fill ? 1 : undefined, display: "flex", flexDirection: "column", gap: 1.25, width: "100%", minWidth: 0, minHeight: fill ? 0 : undefined }}>
          <Box sx={{ flex: fill ? 1 : undefined, display: "grid", gridTemplateColumns: "minmax(88px, 34%) minmax(0, 1fr)", alignItems: "center", columnGap: 2, width: "100%", minWidth: 0 }}>
            <ScoreRing score={score} total={total} passed={passed} />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                columnGap: 2,
                rowGap: 1.25,
                width: "100%",
                minWidth: 0,
              }}
            >
              {scoreFacts(total, passed, failed, undecided).map((fact) => (
                <Box key={fact.label} sx={{ minWidth: 0, textAlign: "center" }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.25, minWidth: 0 }}>
                    <Typography noWrap sx={{ ...metricHeadSx, color: fact.color, minWidth: 0 }}>
                      {fact.label}
                    </Typography>
                    {fact.hint ? (
                      <Tooltip title={fact.hint} arrow placement="top">
                        <Box
                          component="span"
                          tabIndex={0}
                          aria-label={fact.hint}
                          sx={{
                            display: "inline-flex",
                            flexShrink: 0,
                            color: AH_MUTED,
                            lineHeight: 0,
                            outline: "none",
                            "&:focus-visible": { borderRadius: "50%", boxShadow: `0 0 0 2px ${AH_BORDER}` },
                          }}
                        >
                          <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                        </Box>
                      </Tooltip>
                    ) : null}
                  </Box>
                  <Typography noWrap sx={metricValueSx}>
                    {formatCount(fact.value)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
          <Box
            sx={{
              width: "100%",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.25,
              py: 0.75,
              borderRadius: "8px",
              bgcolor: "#eff6ff",
              color: "#1976d2",
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 16, flexShrink: 0 }} />
            <Typography sx={{ flex: 1, minWidth: 0, fontSize: "0.8125rem", fontWeight: 500, lineHeight: 1.4, color: "inherit", textAlign: "left" }}>
              N/A controls are excluded from the calculation.
            </Typography>
          </Box>
          {pendingScan && !Number.isFinite(score) ? <Quiet>{PENDING_SCAN}</Quiet> : null}
        </Box>
      )}
    </Tile>
  );
};

const COLUMNS = ["total", "passed", "failed"];

const MetricRow = ({ cells }) => (
  <Box sx={metricRowSx}>
    {cells.map((cell, index) => (
      <Typography key={COLUMNS[index]} sx={metricValueSx}>
        {cell}
      </Typography>
    ))}
  </Box>
);

const AutomatedControlsCard = ({ passed, failed, known, pendingScan, resources }) => {
  const total = passed + failed;
  const resourceTotal = Number(resources?.total) || 0;
  const resourcePassed = Number(resources?.passed) || 0;
  const resourceFailed = Number(resources?.failed) || 0;
  const controlsPending = pendingScan && total === 0;
  const resourcesMissing = resources == null;
  if (!known) {
    return (
      <Tile label="Automated Controls" align="start" sx={compactTileSx}>
        <Quiet>{dash}</Quiet>
      </Tile>
    );
  }
  if (controlsPending && resourcesMissing) {
    return (
      <Tile label="Automated Controls" align="start" sx={compactTileSx}>
        <Quiet>{PENDING_SCAN}</Quiet>
      </Tile>
    );
  }
  const controlCells = controlsPending ? [dash, dash, dash] : [formatCount(total), formatCount(passed), formatCount(failed)];
  const passedPercent = resourceTotal > 0 ? (resourcePassed / resourceTotal) * 100 : 0;
  const halfSx = {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: 0.75,
  };
  return (
    <Box sx={{ ...cardShellSx, ...compactTileSx, display: "flex", flexDirection: "column", width: "100%", minWidth: 0, boxSizing: "border-box" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))" },
          alignItems: "center",
          width: "100%",
          minWidth: 0,
        }}
      >
        <Box sx={{ ...halfSx, minWidth: 0, pr: { sm: 2 }, overflow: "hidden" }}>
          <Typography noWrap sx={labelSx}>Automated Controls</Typography>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ ...metricRowSx, width: "100%", "& > *": { minWidth: 0 } }}>
              <Typography noWrap sx={{ ...metricHeadSx, color: AH_MUTED }}>Total</Typography>
              <Typography noWrap sx={{ ...metricHeadSx, color: PASSED_TEXT_COLOR }}>Passed</Typography>
              <Typography noWrap sx={{ ...metricHeadSx, color: FAILED_TEXT_COLOR }}>Failed</Typography>
            </Box>
            <Box sx={{ ...metricRowSx, width: "100%", "& > *": { minWidth: 0 } }}>
              {controlCells.map((cell, index) => (
                <Typography key={COLUMNS[index]} noWrap sx={metricValueSx}>
                  {cell}
                </Typography>
              ))}
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            ...halfSx,
            minWidth: 0,
            overflow: "hidden",
            borderTop: { xs: `1px solid ${AH_BORDER}`, sm: "none" },
            borderLeft: { sm: `1px solid ${AH_BORDER}` },
            pt: { xs: 1.25, sm: 0 },
            pl: { sm: 2 },
            mt: { xs: 1.25, sm: 0 },
          }}
        >
          {resourcesMissing ? (
            <Quiet>{pendingScan ? PENDING_SCAN : dash}</Quiet>
          ) : (
            <>
              <Typography noWrap sx={labelSx}>Resource Scanned (Automated): {formatCount(resourceTotal)}</Typography>
              <Tooltip title={`Passed: ${formatCount(resourcePassed)} | Failed: ${formatCount(resourceFailed)}`} arrow placement="top">
                <Box sx={{ height: 7, width: "100%", borderRadius: 5, overflow: "hidden", display: "flex", bgcolor: FAILED_COLOR }}>
                  <Box sx={{ width: `${passedPercent}%`, bgcolor: PASSED_COLOR }} />
                </Box>
              </Tooltip>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: PASSED_TEXT_COLOR, whiteSpace: "nowrap" }}>
                  {formatCount(resourcePassed)} Passed
                </Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: FAILED_TEXT_COLOR, whiteSpace: "nowrap" }}>
                  {formatCount(resourceFailed)} Failed
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const ManualControlsCard = ({ components, known, review }) => {
  const total = components.manualPassed + components.manualFailed + components.notApplicable + components.undecided;
  const maxReview = Math.max(...review.map((item) => item.count), 1);
  const shellSx = {
    ...cardShellSx,
    ...compactTileSx,
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
  };
  if (!known || (total <= 0 && !review.length)) {
    return (
      <Box sx={shellSx}>
        <Typography noWrap sx={labelSx}>Manual Controls</Typography>
        <Quiet>{!known ? dash : "No manual controls"}</Quiet>
      </Box>
    );
  }
  return (
    <Box sx={shellSx}>
      <Box
        sx={{
          display: "grid",
          flex: 1,
          alignItems: "stretch",
          minWidth: 0,
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            sm: total > 0 && review.length ? "max-content minmax(0, 1fr)" : "minmax(0, 1fr)",
          },
        }}
      >
        {total > 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, minWidth: 0, alignItems: "center", pr: review.length ? "10px" : 0 }}>
            <Typography noWrap sx={{ ...labelSx, alignSelf: "flex-start" }}>Manual Controls</Typography>
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center" }}>
              <Typography noWrap sx={{ ...metricHeadSx, color: AH_MUTED }}>Total</Typography>
              <Typography noWrap sx={{ ...metricValueSx, fontSize: 28, lineHeight: 1.1 }}>{formatCount(total)}</Typography>
            </Box>
          </Box>
        ) : null}
        {review.length ? (
          <Box
            sx={{
              minWidth: 0,
              minHeight: 0,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 0.75,
              borderTop: { xs: total > 0 ? `1px solid ${AH_BORDER}` : "none", sm: "none" },
              borderLeft: { sm: total > 0 ? `1px solid ${AH_BORDER}` : "none" },
              pt: { xs: total > 0 ? 1 : 0, sm: 0 },
              pl: { xs: 0, sm: total > 0 ? "10px" : 0 },
            }}
          >
            <Typography noWrap sx={labelSx}>Review status</Typography>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  width: "100%",
                  display: "grid",
                  gridTemplateColumns: `repeat(${review.length}, minmax(0, 1fr))`,
                  columnGap: 1,
                  alignItems: "stretch",
                }}
              >
                {review.map((status) => {
                  const share = (status.count / maxReview) * 100;
                  return (
                    <Tooltip key={status.value} title={`${status.label}: ${formatCount(status.count)}`} arrow placement="top">
                      <Box sx={{ minWidth: 0, height: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                        <Typography noWrap sx={{ ...metricValueSx, lineHeight: 1.2, color: status.count > 0 ? AH_TEXT : AH_MUTED }}>
                          {formatCount(status.count)}
                        </Typography>
                        <Box
                          sx={{
                            width: 28,
                            minWidth: 28,
                            maxWidth: 28,
                            height: 72,
                            minHeight: 72,
                            maxHeight: 72,
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "flex-end",
                            bgcolor: AH_TRACK,
                            borderRadius: "4px 4px 0 0",
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              width: "100%",
                              height: `${share}%`,
                              minHeight: status.count > 0 ? 3 : 0,
                              bgcolor: status.color,
                              borderRadius: "4px 4px 0 0",
                            }}
                          />
                        </Box>
                        <Typography
                          sx={{
                            ...metricHeadSx,
                            color: AH_MUTED,
                            width: "100%",
                            minHeight: "2.4em",
                            lineHeight: 1.15,
                            whiteSpace: "normal",
                            overflowWrap: "break-word",
                          }}
                        >
                          {status.value === "NOT_APPLICABLE" ? "N/A" : status.label}
                        </Typography>
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            </Box>
          ) : null}
        </Box>
      </Box>
  );
};

/**
 * One account on one framework: compliance score, automated results, and manual review.
 * With `onToggle`, only the header shows until it is expanded; `children` expand with the metrics.
 */
const AuditAccountCard = ({
  scope,
  audit,
  resources,
  compliance,
  scanDate,
  accountNumber,
  accountSwitcher,
  metricsStatus = "ready",
  expanded = true,
  onToggle,
  children,
}) => {
  const bodyId = useId();
  const collapsible = typeof onToggle === "function";
  const open = !collapsible || expanded;
  const known = metricsStatus === "ready" && Boolean(scope);
  const unavailable =
    metricsStatus === "error"
      ? "Assessment metrics are unavailable"
      : metricsStatus === "ready" && !scope
        ? "Framework not assessed on this account"
        : "";
  const pendingScan = known && !scope.hasScanResult;
  const components = known ? readComponents(scope.components) : readComponents(null);
  const scoreParts = complianceScoreParts(components);
  const accounts = Array.isArray(audit?.accounts) ? audit.accounts : [];
  const account = accounts.find((item) => String(item?.accountNumber) === String(accountNumber));
  const accountName =
    account?.accountName && String(account.accountName) !== String(accountNumber) ? account.accountName : "";
  const accountViewLabel = accountName
    ? `${accountName}, ${accountNumber} View`
    : accountNumber
      ? `${accountNumber} View`
      : "Account View";
  const review = useMemo(
    () => reviewRows(known ? statusCountsForAccount(audit, accountNumber) : null),
    [known, audit, accountNumber]
  );

  const accountAction =
    accountSwitcher || (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          minHeight: 38,
          px: 1.5,
          borderRadius: "8px",
          backgroundColor: alpha(AH_ACCENT, 0.06),
          border: `1.5px solid ${alpha(AH_ACCENT, 0.55)}`,
          maxWidth: "100%",
        }}
      >
        <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: AH_TEXT, whiteSpace: "nowrap" }}>
          Account: {accountNumber}
        </Typography>
      </Box>
    );

  const metrics = (
    <Box sx={accountGridSx}>
      <ComplianceScoreCard
        total={scoreParts.total}
        passed={scoreParts.passed}
        failed={scoreParts.failed}
        undecided={scoreParts.undecided}
        known={known}
        unavailable={unavailable}
        pendingScan={pendingScan}
        fill
      />
      <Box sx={sideStackSx}>
        <AutomatedControlsCard
          passed={components.autoPassed}
          failed={components.autoFailed}
          known={known}
          pendingScan={pendingScan}
          resources={resources}
        />
        <ManualControlsCard components={components} known={known} review={review} />
      </Box>
    </Box>
  );

  const header = (
    <CardHeader
      eyebrow={accountViewLabel}
      compliance={compliance}
      meta={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: { xs: "wrap", md: "nowrap" },
            columnGap: 1.5,
            rowGap: 0.5,
            whiteSpace: "nowrap",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <CalendarMonthIcon sx={{ color: AH_MUTED, fontSize: 14 }} />
            <span>Scan date: {scanDate ? formatDateTime(scanDate) : "—"}</span>
          </Box>
          {!open ? (
            <>
              <MetaSeparator />
              <span>
                Compliance Score:{" "}
                <Box component="span" sx={{ color: AH_TEXT, fontWeight: 700 }}>
                  {Number.isFinite(scoreParts.score) ? `${scoreParts.score.toFixed(2)}%` : dash}
                </Box>
              </span>
            </>
          ) : null}
        </Box>
      }
      action={
        collapsible ? (
          // Clicks in the switcher, including its popup, must not toggle the card.
          <Box
            onClick={(event) => event.stopPropagation()}
            sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1, width: "100%", minWidth: 0 }}
          >
            <Box sx={{ display: "flex", justifyContent: "flex-end", flex: "1 1 auto", minWidth: 0 }}>{accountAction}</Box>
            <Tooltip title={open ? "Hide details" : "Show details"} arrow placement="top">
              <IconButton
                size="small"
                onClick={onToggle}
                aria-expanded={open}
                aria-controls={bodyId}
                aria-label={open ? "Hide assessment details" : "Show assessment details"}
                sx={{ flexShrink: 0, color: AH_MUTED, border: `1px solid ${AH_BORDER}`, bgcolor: AH_WHITE, "&:hover": { bgcolor: AH_TRACK, color: AH_TEXT } }}
              >
                <ExpandMoreRoundedIcon
                  fontSize="small"
                  sx={{ transition: "transform 0.2s ease", transform: open ? "rotate(180deg)" : "none" }}
                />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          accountAction
        )
      }
    />
  );

  if (!collapsible) {
    return (
      <CardShell sx={{ p: 1.5, gap: 1 }}>
        {header}
        {metrics}
      </CardShell>
    );
  }

  return (
    <CardShell sx={{ p: 1.5, gap: 0 }}>
      <Box onClick={onToggle} sx={{ cursor: "pointer" }}>
        {header}
      </Box>
      <Collapse in={open} timeout="auto" mountOnEnter id={bodyId}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          {metrics}
          {children ? <Divider sx={{ borderColor: AH_BORDER }} /> : null}
          {children}
        </Box>
      </Collapse>
    </CardShell>
  );
};

export default AuditAccountCard;
